import React, {
  useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputPrivacy from '../../../common/componentsV2/inputs/InputPrivacy';
import { greeniaPrivacyPolicy, modifyRequestData } from '../../../services/axios-client/axeCommons';
import { FormInputDate } from '../../../common/formInputsV2/FormInputDate';
import { FormInputAddressComplete } from '../../../common/formInputsV2/FormInputAddressComplete';
import { FormInputAddressCompleteManual } from '../../../common/formInputsV2/FormInputAddressCompleteManual';
import { FormInputText } from '../../../common/formInputsV2/FormInputText';
import FormReactSelectInputBirthPlace from '../../../common/formInputsV2/FormReactSelectInputBirthPlace';
import getMinAllowedBirthday from '../../../common/helpers/getMinAllowedBirthday';
import { FormInputRadio } from '../../../common/formInputsV2/FormInputRadio';
import {
  genders, mediciLiberiProfessionistiRetroactivities, mlpMassimali,
} from '../../../common/constants';
import PageLoading from '../../../common/componentsV2/elements/PageLoading';
import generateFiscalCode from '../../../common/helpers/generateFiscalCode';
import {
  getFastQuoteData,
  mlpSubmitAnswers,
  professionaleSanitaGetInitialData, resetProfessionaleSanitaForm,
} from '../../../features/forms/formsActions';
import { mediciLiberiProfessionistiSchema } from '../MediciLiberiProfessionisti.validations';
import { FormInputSelect } from '../../../common/formInputsV2/FormInputSelect';
import MLPActivities from './MLPActivities';
import { ProfessionaleSanitaFormFlowStyles, ProfessionaleSanitaFormStyles } from '../styles/ProfessionaleSanitaFormFlow.styles';
import { getBlockingFields, buildMissingFieldsMessage } from '../MediciLiberiProfessionisti.stepErrors';
import { V2FontImport } from '../../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../../common/formInputsV2/FormLayout.styles';
import { PageHeading, PageHeadingTitle, PageHeadingSubtitle } from '../../../common/formInputsV2/PageHeading.styles';
import { SurfaceCardHeading } from '../../../common/formInputsV2/Card.styles';
import { FooterNav } from '../../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, AddItemButton } from '../../../common/formInputsV2/Button.styles';
import { WarningAlert } from '../../../common/formInputsV2/Alert.styles';
import { sonnerErrorAlert } from '../../../common/utils/sonnerAlerts';

function MediciLiberiProfessionisti({ selectedSubProduct }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { fastQuoteId } = useParams();
  const [fastQuoteData, setFastQuoteData] = useState(null);
  const skipCheckGenderRef = useRef(null);
  const state = useSelector((store) => store.forms.professionale_sanita);
  const [showManualAdress, setShowManualAdress] = useState(false);
  const {
    loading,
    states,
    municipalities,
    dataPrivacy,
  } = state;
  const {
    handleSubmit,
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    getValues,
    watch,
    control,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(mediciLiberiProfessionistiSchema),
    shouldFocusError: false,
    defaultValues: {
      product: 'rc_professionale_sanita',
      sub_product: selectedSubProduct,
      source: 'greenia.it',
      policy_effective_date: '',
      privacies: [],
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      address: '',
      house_number: '',
      postal_code: '',
      phone: '',
      gender: '',
      date_of_birth: '',
      country_of_birth_code: 'Z000',
      province_of_birth_code: null,
      commune_of_birth_code: null,
      born_abroad: false,
      residence_commune_code: '',
      residence_province_code: '',
      active_customer_id: null,
      existing_customer: false,
      retroactivity: '',
      massimale: '',
      activities: [
        {
          activity_type: '',
          activity: '',
        },
      ],
      albo_registration_number: '',
    },
  });

  const {
    fields, append, remove, replace,
  } = useFieldArray({
    control,
    name: 'activities',
  });

  const formDataFlow = watch();

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  // Dot-path aware so it also works for nested field-array paths like
  // "activities.0.activity_type" — a plain errors[name]/touchedFields[name]
  // bracket lookup never matches RHF's actual nested error/touched shape
  // for those, so the field would never show as valid/green.
  const getFieldByPath = (obj, path) => path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);

  const isValid = (name) => {
    if (getFieldByPath(errors, name)?.message !== undefined) {
      return false;
    }

    if (getFieldByPath(touchedFields, name) === undefined) {
      return false;
    }

    return true;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  // handleSubmit's onInvalid callback — the whole form is one "step", so
  // every blocking field across it goes into a single toast instead of a
  // silent (or auto-focus-jumping) failed submit.
  const handleValidationErrors = (formErrors) => {
    const blocking = getBlockingFields(formErrors);
    sonnerErrorAlert(buildMissingFieldsMessage(blocking));
    scrollToFirstError();
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formDataFlow.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formDataFlow.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  useEffect(() => {
    professionaleSanitaGetInitialData();

    return () => {
      resetProfessionaleSanitaForm();
    };
  }, []);

  const getFiscalCode = (birthDate) => {
    const d = birthDate.split('-');
    const fData = {
      name: formDataFlow.name,
      surname: formDataFlow.surname,
      gender: formDataFlow.gender,
      day: d[2],
      month: d[1],
      year: d[0],
    };
    if (formDataFlow.born_abroad) {
      fData.birthplace = states.filter(
        (s) => s.state_code === formDataFlow.country_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = 'EE';
    } else {
      fData.birthplace = municipalities.filter(
        (municipality) => municipality.cadastral_code === formDataFlow.commune_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = formDataFlow.province_of_birth_code;
    }
    return generateFiscalCode(fData);
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formDataFlow }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const submitForm = async (formDataFlow) => {
    const { error, message } = validateDataPrivacy();

    if (error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(message);
      scrollToFirstError();
      return;
    }

    const _userData = { ...formDataFlow };

    if (_userData.born_abroad && _userData.country_of_birth_code === 'Z000') {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
      return;
    }

    if (
      _userData.born_abroad
      && (_userData.country_of_birth_code === null
        || _userData.country_of_birth_code === ''
        || _userData.country_of_birth_code == undefined)
    ) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }

    if (
      formDataFlow.date_of_birth !== ''
      && formDataFlow.date_of_birth !== null
    ) {
      _userData.date_of_birth = moment(formDataFlow.date_of_birth).format(
        'YYYY-MM-DD',
      );
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = moment(
      formDataFlow.policy_effective_date,
    ).format('YYYY-MM-DD');
    _userData.fiscal_code = formDataFlow.gender !== 'G'
      ? getFiscalCode(formDataFlow.date_of_birth)
      : null;
    delete _userData.existing_customer;

    mlpSubmitAnswers(_userData, navigate);
  };

  useEffect(() => {
    if (skipCheckGenderRef.current === true) {
      skipCheckGenderRef.current = false;
      return;
    }

    const triggerValidation = {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    };

    if (
      ['M', 'F'].includes(formDataFlow.gender)
      && formDataFlow.date_of_birth === null
    ) {
      setValue('date_of_birth', '', triggerValidation);
      setValue('country_of_birth_code', 'Z000', triggerValidation);
      setValue('province_of_birth_code', null, triggerValidation);
      setValue('commune_of_birth_code', null, triggerValidation);
      setValue('born_abroad', false, triggerValidation);
      setValue('name', '', triggerValidation);
      setValue('surname', '', triggerValidation);
    }
  }, [formDataFlow.gender, formDataFlow.date_of_birth]);

  useEffect(() => {
    const values = getValues();

    if (values !== undefined) {
      const objectKeys = Object.keys(values);

      objectKeys.forEach((key) => {
        if (values[key] !== '' && values[key] !== null) {
          setValue(key, values[key], {
            shouldTouch: true,
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    const callAsync = async () => {
      if (
        location.state !== null
        && location.state.requestToken !== undefined
      ) {
        skipCheckGenderRef.current = true;
        const {
          data: {
            data: { userData },
          },
        } = await modifyRequestData(location.state.requestToken);
        const { activities, ...restUserData } = userData;
        // Only touch fields that actually have a value — touching an empty
        // optional field (e.g. albo_registration_number) would mark it
        // valid/green even though it's blank.
        const filledUserData = Object.fromEntries(
          Object.entries(restUserData).filter(
            ([, value]) => value !== '' && value !== null && value !== undefined,
          ),
        );
        updateFormData(filledUserData);

        if (Array.isArray(activities) && activities.length > 0) {
          replace(activities);
          activities.forEach((activity, index) => {
            if (activity?.activity_type) {
              setValue(`activities.${index}.activity_type`, activity.activity_type, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }
            if (activity?.activity) {
              setValue(`activities.${index}.activity`, activity.activity, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }
          });
        }
      }
    };

    callAsync();
  }, [location]);

  useEffect(() => {
    if (fastQuoteId && fastQuoteData === null) {
      getFastQuoteData(fastQuoteId).then((fast_quote_response) => {
        setFastQuoteData(fast_quote_response.data);
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Error fetching fast quote data:', error);
      });
    }

    if (fastQuoteData) {
      updateFormData({
        retroactivity: fastQuoteData.reference_response.retroactivity,
        massimale: fastQuoteData.reference_response.massimale,
      });

      setTimeout(() => {
        const { activities } = fastQuoteData.reference_response;
        if (Array.isArray(activities) && activities.length > 0) {
          replace(activities);
          activities.forEach((activity, index) => {
            if (activity?.activity_type) {
              setValue(`activities.${index}.activity_type`, activity.activity_type, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }
            if (activity?.activity) {
              setValue(`activities.${index}.activity`, activity.activity, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }
          });
        }
      }, 500);
    }
  }, [fastQuoteData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return <PageLoading />;
  }

  return (
    <ProfessionaleSanitaFormFlowStyles>
      <ProfessionaleSanitaFormStyles
        onSubmit={handleSubmit(submitForm, handleValidationErrors)}
        noValidate
      >
        <V2FontImport />
        <PageHeading>
          <PageHeadingTitle>Medici Liberi Professionisti</PageHeadingTitle>
          <PageHeadingSubtitle>Compila i dati per generare il preventivo</PageHeadingSubtitle>
        </PageHeading>
        <div className="salute-tutorial">

          <FormLayout className="form-container">
            <FormInputRadio
              options={genders.filter((g) => g.value !== 'G')}
              label="Il contraente è"
              name="gender"
              registration={register('gender')}
              error={errors.gender?.message}
            />

            <div>
              <div>
                <FormInputText
                  placeholder="Nome"
                  label="Nome"
                  registration={register('name')}
                  error={errors.name?.message}
                  valid={isValid('name')}
                />
                <FormInputText
                  placeholder="Cognome"
                  label="Cognome"
                  registration={register('surname')}
                  error={errors.surname?.message}
                  valid={isValid('surname')}
                />
                <FormInputDate
                  label="Data di nascita"
                  registration={register('date_of_birth')}
                  minDate={getMinAllowedBirthday()}
                  maxDate="2021-01-01"
                  error={errors.date_of_birth?.message}
                  valid={isValid('date_of_birth')}
                />
                <FormReactSelectInputBirthPlace
                  states={states}
                  options={municipalities}
                  label="Luogo di nascita"
                  name="commune_of_birth_code"
                  values={{
                    commune_of_birth_code: formDataFlow.commune_of_birth_code,
                    province_of_birth_code:
                      formDataFlow.province_of_birth_code,
                    born_abroad: formDataFlow.born_abroad,
                    country_of_birth_code: formDataFlow.country_of_birth_code,
                  }}
                  onChange={(value) => updateFormData({
                    commune_of_birth_code: value.commune_of_birth_code,
                    province_of_birth_code: value.province_of_birth_code,
                    born_abroad: value.born_abroad,
                    country_of_birth_code: value.country_of_birth_code,
                  })}
                  valid={isValid('commune_of_birth_code')}
                  error={errors.commune_of_birth_code?.message}
                  valid_country_of_birth_code={isValid(
                    'country_of_birth_code',
                  )}
                  error_country_of_birth_code={
                    errors.country_of_birth_code?.message
                  }
                />
              </div>
            </div>

            <FormInputText
              registration={register('albo_registration_number')}
              label="Numero iscrizione Albo (Facoltativo)"
              error={errors.albo_registration_number?.message}
              valid={isValid('albo_registration_number')}
            />

            <FormInputSelect
              label="Retroattività"
              registration={register('retroactivity')}
              error={errors.retroactivity?.message}
              valid={isValid('retroactivity')}
              placeholder="-Seleziona-"
              options={mediciLiberiProfessionistiRetroactivities}
            />

            <FormInputSelect
              label="Massimale"
              registration={register('massimale')}
              error={errors.massimale?.message}
              valid={isValid('massimale')}
              placeholder="-Seleziona-"
              options={mlpMassimali}
            />
            {formDataFlow.massimale !== '' && formDataFlow.retroactivity !== '' && (
              <div>
                {!formDataFlow.activities?.some((a) => a.activity_type && a.activity) && (
                  <WarningAlert>
                    <FontAwesomeIcon icon={faWarning} />
                    <span>È richiesta almeno un&apos;attività!</span>
                  </WarningAlert>
                )}
                {fields.map((field, index) => (
                  <MLPActivities
                    control={control}
                    formDataFlow={formDataFlow}
                    key={field.id}
                    index={index}
                    remove={remove}
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    isValid={isValid}
                  />
                ))}

                {fields.length < 4 && (
                  <div className="d-table ms-auto mt-2">
                    <AddItemButton type="button" onClick={() => append({ activity_type: '', activity: '' })}>
                      <FontAwesomeIcon icon={faPlus} />
                      Aggiungi Attività
                    </AddItemButton>
                  </div>
                )}
              </div>
            )}

            <FormInputText
              registration={register('email')}
              label="E-mail"
              placeholder="nome@esempio.it"
              error={errors.email?.message}
              valid={isValid('email')}
            />
            <FormInputText
              registration={register('phone')}
              label="Phone"
              placeholder="Es. 3401234567"
              paragraph="Numeri di rete fissa non accettati dalle aziende"
              error={errors.phone?.message}
              valid={isValid('phone')}
            />

            {showManualAdress ? (
              <FormInputAddressCompleteManual
                municipalities={municipalities}
                formData={formDataFlow}
                isValid={isValid}
                errors={errors}
                register={register}
                updateFormData={updateFormData}
              />
            ) : (
              <FormInputAddressComplete
                municipalities={municipalities}
                formData={formDataFlow}
                isValid={isValid}
                errors={errors}
                register={register}
                updateFormData={updateFormData}
                addressId="autocomplete-address-contractor"
                setShow={setShowManualAdress}
              />
            )}

            <FormInputDate
              label="Data Decorrenza"
              registration={register('policy_effective_date')}
              minDate={moment().format('YYYY-MM-DD')}
              error={errors.policy_effective_date?.message}
              valid={isValid('policy_effective_date')}
            />
          </FormLayout>
        </div>

        <FormLayout className="terms-container">
          <div className="terms">
            <SurfaceCardHeading>Informativa Privacy e IVASS</SurfaceCardHeading>
            <div className="text-decoration-underline mt-1 mb-2">
              <a
                onClick={() => greeniaPrivacyPolicy()}
                style={{ cursor: 'pointer' }}
              >
                <small>Greenia Privacy Policy</small>
              </a>
            </div>
            {dataPrivacy.map((privacy, i) => (
              <InputPrivacy
                required={privacy.required}
                label={privacy.content}
                name={privacy.type}
                id={privacy.type}
                key={i}
                checked={formDataFlow?.privacies?.includes(privacy.id)}
                hasError={showPrivacyErrors && privacy.required === 1 && !formDataFlow?.privacies?.includes(privacy.id)}
                onChange={(value) => privacyItemChangeHandler(value, privacy)}
              />
            ))}
          </div>
        </FormLayout>

        <FooterNav className="footer-buttons button-profession-step">
          <div className="d-flex justify-content-between">
            <PrimaryButton type="submit" className="btn btn-questionnaire">
              Vai a preventivo
            </PrimaryButton>
          </div>
        </FooterNav>

      </ProfessionaleSanitaFormStyles>
    </ProfessionaleSanitaFormFlowStyles>
  );
}

export default MediciLiberiProfessionisti;
